<!-- IncidentResponsePlanModal.vue -->
<template>
  <div class="modal fade" id="incidentResponsePlanModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h4 class="modal-title">
            <i class="mdi mdi-shield-alert-outline me-2"></i>
            Incident Detection & Disaster Recovery Plan
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Plan Navigation -->
          <ul class="nav nav-tabs mb-4" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#overview" role="tab">
                <i class="mdi mdi-file-document me-1"></i>
                Overview
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#incident-response" role="tab">
                <i class="mdi mdi-shield-alert me-1"></i>
                Incident Response
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#disaster-recovery" role="tab">
                <i class="mdi mdi-backup-restore me-1"></i>
                Disaster Recovery
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#security-breaches" role="tab">
                <i class="mdi mdi-security me-1"></i>
                Security Breaches
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#contacts" role="tab">
                <i class="mdi mdi-phone me-1"></i>
                Emergency Contacts
              </a>
            </li>
          </ul>

          <div class="tab-content">
            <!-- Overview Tab -->
            <div class="tab-pane fade show active" id="overview" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card border-warning">
                    <div class="card-header bg-warning text-dark">
                      <h5 class="mb-0">
                        <i class="mdi mdi-information-outline me-2"></i>
                        County Revenue Management System - Incident Response Overview
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <h6>Plan Scope & Purpose</h6>
                          <p>This Incident Response and Disaster Recovery Plan (IRP/DRP) provides comprehensive guidelines for:</p>
                          <ul>
                            <li>Responding to security incidents and system failures</li>
                            <li>Recovering from catastrophic hardware failures</li>
                            <li>Handling data corruption and loss scenarios</li>
                            <li>Managing security breaches and cyber attacks</li>
                            <li>Ensuring business continuity during emergencies</li>
                          </ul>

                          <h6 class="mt-4">Response Time Objectives</h6>
                          <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                              <thead class="table-dark">
                              <tr>
                                <th>Severity</th>
                                <th>Response Time</th>
                                <th>Recovery Time</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr>
                                <td><span class="badge bg-danger">Critical</span></td>
                                <td>15 minutes</td>
                                <td>4 hours</td>
                              </tr>
                              <tr>
                                <td><span class="badge bg-warning">High</span></td>
                                <td>30 minutes</td>
                                <td>8 hours</td>
                              </tr>
                              <tr>
                                <td><span class="badge bg-info">Medium</span></td>
                                <td>2 hours</td>
                                <td>24 hours</td>
                              </tr>
                              <tr>
                                <td><span class="badge bg-success">Low</span></td>
                                <td>4 hours</td>
                                <td>72 hours</td>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div class="col-md-6">
                          <h6>Critical System Components</h6>
                          <div class="component-list">
                            <div class="component-item mb-3 p-3 border rounded">
                              <div class="d-flex align-items-center">
                                <i class="mdi mdi-database text-primary fs-4 me-3"></i>
                                <div>
                                  <strong>Database Servers</strong>
                                  <p class="mb-0 text-muted">PostgreSQL Primary & Replica servers</p>
                                  <small class="text-success">RTO: 2 hours | RPO: 15 minutes</small>
                                </div>
                              </div>
                            </div>

                            <div class="component-item mb-3 p-3 border rounded">
                              <div class="d-flex align-items-center">
                                <i class="mdi mdi-server text-info fs-4 me-3"></i>
                                <div>
                                  <strong>Application Servers</strong>
                                  <p class="mb-0 text-muted">Web and API application servers</p>
                                  <small class="text-success">RTO: 1 hour | RPO: 30 minutes</small>
                                </div>
                              </div>
                            </div>

                            <div class="component-item mb-3 p-3 border rounded">
                              <div class="d-flex align-items-center">
                                <i class="mdi mdi-security text-warning fs-4 me-3"></i>
                                <div>
                                  <strong>Security Infrastructure</strong>
                                  <p class="mb-0 text-muted">Firewall, IDS/IPS, Authentication</p>
                                  <small class="text-success">RTO: 30 minutes | RPO: 0</small>
                                </div>
                              </div>
                            </div>

                            <div class="component-item mb-3 p-3 border rounded">
                              <div class="d-flex align-items-center">
                                <i class="mdi mdi-backup-restore text-success fs-4 me-3"></i>
                                <div>
                                  <strong>Backup Systems</strong>
                                  <p class="mb-0 text-muted">Automated backup and recovery</p>
                                  <small class="text-success">RTO: 4 hours | RPO: 1 hour</small>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="alert alert-info">
                            <strong>Note:</strong> RTO = Recovery Time Objective, RPO = Recovery Point Objective
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Incident Response Tab -->
            <div class="tab-pane fade" id="incident-response" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card border-danger">
                    <div class="card-header bg-danger text-white">
                      <h5 class="mb-0 text-white">
                        <i class="mdi mdi-alert-circle me-2"></i>
                        Incident Response Procedures
                      </h5>
                    </div>
                    <div class="card-body">
                      <!-- Incident Classification -->
                      <div class="mb-4">
                        <h6>Step 1: Incident Detection & Classification</h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="incident-type p-3 border rounded mb-3">
                              <h6 class="text-danger">
                                <i class="mdi mdi-security me-2"></i>
                                Security Incidents
                              </h6>
                              <ul class="mb-0">
                                <li>Unauthorized access attempts</li>
                                <li>Malware detection</li>
                                <li>Data breach indicators</li>
                                <li>DDoS attacks</li>
                                <li>Insider threats</li>
                              </ul>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="incident-type p-3 border rounded mb-3">
                              <h6 class="text-warning">
                                <i class="mdi mdi-server-off me-2"></i>
                                System Failures
                              </h6>
                              <ul class="mb-0">
                                <li>Application server crashes</li>
                                <li>Database connectivity issues</li>
                                <li>Network outages</li>
                                <li>Performance degradation</li>
                                <li>Service unavailability</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Response Steps -->
                      <div class="mb-4">
                        <h6>Step 2: Immediate Response Actions</h6>
                        <div class="response-timeline">
                          <div class="timeline-item mb-3">
                            <div class="timeline-marker bg-danger text-white">1</div>
                            <div class="timeline-content">
                              <h6>Immediate Assessment (0-5 minutes)</h6>
                              <ul>
                                <li>Confirm incident occurrence and severity</li>
                                <li>Document initial findings and timestamps</li>
                                <li>Activate incident response team</li>
                                <li>Isolate affected systems if necessary</li>
                              </ul>
                            </div>
                          </div>

                          <div class="timeline-item mb-3">
                            <div class="timeline-marker bg-warning text-dark">2</div>
                            <div class="timeline-content">
                              <h6>Containment (5-15 minutes)</h6>
                              <ul>
                                <li>Implement immediate containment measures</li>
                                <li>Preserve evidence and system state</li>
                                <li>Notify key stakeholders</li>
                                <li>Begin detailed investigation</li>
                              </ul>
                            </div>
                          </div>

                          <div class="timeline-item mb-3">
                            <div class="timeline-marker bg-info text-white">3</div>
                            <div class="timeline-content">
                              <h6>Investigation & Analysis (15-60 minutes)</h6>
                              <ul>
                                <li>Conduct thorough system analysis</li>
                                <li>Identify root cause and impact scope</li>
                                <li>Collect forensic evidence</li>
                                <li>Determine recovery strategy</li>
                              </ul>
                            </div>
                          </div>

                          <div class="timeline-item mb-3">
                            <div class="timeline-marker bg-success text-white">4</div>
                            <div class="timeline-content">
                              <h6>Recovery & Restoration (1-4 hours)</h6>
                              <ul>
                                <li>Execute recovery procedures</li>
                                <li>Restore affected services</li>
                                <li>Validate system integrity</li>
                                <li>Monitor for recurring issues</li>
                              </ul>
                            </div>
                          </div>

                          <div class="timeline-item">
                            <div class="timeline-marker bg-primary text-white">5</div>
                            <div class="timeline-content">
                              <h6>Post-Incident Review (24-48 hours)</h6>
                              <ul>
                                <li>Conduct lessons learned session</li>
                                <li>Update procedures and documentation</li>
                                <li>Implement preventive measures</li>
                                <li>File incident report</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Automated Response -->
                      <div class="alert alert-success">
                        <h6 class="alert-heading">
                          <i class="mdi mdi-robot me-2"></i>
                          Automated Response Capabilities
                        </h6>
                        <ul class="mb-0">
                          <li><strong>Threat Detection:</strong> Real-time monitoring with automatic threat identification</li>
                          <li><strong>Immediate Isolation:</strong> Automatic quarantine of compromised systems</li>
                          <li><strong>Alert Distribution:</strong> Instant notifications to response team members</li>
                          <li><strong>Evidence Collection:</strong> Automated log collection and preservation</li>
                          <li><strong>Failover Activation:</strong> Automatic switching to backup systems</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Disaster Recovery Tab -->
            <div class="tab-pane fade" id="disaster-recovery" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <h5 class="mb-0 text-white">
                        <i class="mdi mdi-backup-restore me-2"></i>
                        Disaster Recovery Procedures
                      </h5>
                    </div>
                    <div class="card-body">
                      <!-- Hardware Failure Recovery -->
                      <div class="mb-4">
                        <h6 class="text-danger">
                          <i class="mdi mdi-server-off me-2"></i>
                          Hardware Failure Recovery
                        </h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="recovery-scenario p-3 border rounded">
                              <h6>Primary Database Server Failure</h6>
                              <div class="recovery-steps">
                                <div class="step-item mb-2">
                                  <span class="step-number bg-danger text-white">1</span>
                                  <span>Automatic failover to replica server (2 minutes)</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-warning text-dark">2</span>
                                  <span>Validate data integrity and connections</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-info text-white">3</span>
                                  <span>Update DNS and load balancer configurations</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-success text-white">4</span>
                                  <span>Monitor performance and establish new replica</span>
                                </div>
                              </div>
                              <div class="recovery-metrics mt-3">
                                <small class="text-muted">
                                  <strong>RTO:</strong> 15 minutes | <strong>RPO:</strong> 5 minutes
                                </small>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-6">
                            <div class="recovery-scenario p-3 border rounded">
                              <h6>Application Server Cluster Failure</h6>
                              <div class="recovery-steps">
                                <div class="step-item mb-2">
                                  <span class="step-number bg-danger text-white">1</span>
                                  <span>Activate standby application servers</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-warning text-dark">2</span>
                                  <span>Deploy latest application code</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-info text-white">3</span>
                                  <span>Restore session data from Redis cluster</span>
                                </div>
                                <div class="step-item mb-2">
                                  <span class="step-number bg-success text-white">4</span>
                                  <span>Verify all services and integrations</span>
                                </div>
                              </div>
                              <div class="recovery-metrics mt-3">
                                <small class="text-muted">
                                  <strong>RTO:</strong> 30 minutes | <strong>RPO:</strong> 0 minutes
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Data Corruption Recovery -->
                      <div class="mb-4">
                        <h6 class="text-warning">
                          <i class="mdi mdi-database-alert me-2"></i>
                          Data Corruption Recovery
                        </h6>
                        <div class="corruption-recovery p-3 bg-light rounded">
                          <h6>Database Corruption Detection & Recovery</h6>
                          <div class="row">
                            <div class="col-md-4">
                              <div class="detection-phase">
                                <h6 class="text-info">Detection Phase</h6>
                                <ul>
                                  <li>Automated integrity checks every 15 minutes</li>
                                  <li>Checksum validation on critical tables</li>
                                  <li>Transaction log analysis</li>
                                  <li>Application error monitoring</li>
                                </ul>
                              </div>
                            </div>
                            <div class="col-md-4">
                              <div class="isolation-phase">
                                <h6 class="text-warning">Isolation Phase</h6>
                                <ul>
                                  <li>Immediate read-only mode activation</li>
                                  <li>Stop all write operations</li>
                                  <li>Identify corruption scope</li>
                                  <li>Preserve uncorrupted data</li>
                                </ul>
                              </div>
                            </div>
                            <div class="col-md-4">
                              <div class="recovery-phase">
                                <h6 class="text-success">Recovery Phase</h6>
                                <ul>
                                  <li>Point-in-time recovery from backups</li>
                                  <li>Transaction replay from logs</li>
                                  <li>Data validation and verification</li>
                                  <li>Gradual service restoration</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Backup Strategy -->
                      <div class="mb-4">
                        <h6 class="text-success">
                          <i class="mdi mdi-backup-restore me-2"></i>
                          Backup & Recovery Strategy
                        </h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="backup-schedule p-3 border rounded">
                              <h6>Automated Backup Schedule</h6>
                              <div class="table-responsive">
                                <table class="table table-sm">
                                  <thead class="table-dark">
                                  <tr>
                                    <th>Type</th>
                                    <th>Frequency</th>
                                    <th>Retention</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  <tr>
                                    <td>Transaction Log</td>
                                    <td>Every 15 min</td>
                                    <td>7 days</td>
                                  </tr>
                                  <tr>
                                    <td>Incremental</td>
                                    <td>Every 6 hours</td>
                                    <td>30 days</td>
                                  </tr>
                                  <tr>
                                    <td>Full Backup</td>
                                    <td>Daily</td>
                                    <td>90 days</td>
                                  </tr>
                                  <tr>
                                    <td>Archive</td>
                                    <td>Monthly</td>
                                    <td>7 years</td>
                                  </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-6">
                            <div class="backup-locations p-3 border rounded">
                              <h6>Backup Storage Locations</h6>
                              <ul>
                                <li><strong>Primary:</strong> On-site SAN storage (RAID 10)</li>
                                <li><strong>Secondary:</strong> Off-site data center (50km)</li>
                                <li><strong>Cloud:</strong> AWS S3 with encryption</li>
                                <li><strong>Archive:</strong> Cold storage (Glacier)</li>
                              </ul>

                              <div class="backup-verification mt-3">
                                <h6>Verification Process</h6>
                                <ul>
                                  <li>Daily backup integrity tests</li>
                                  <li>Weekly restore simulations</li>
                                  <li>Monthly full recovery drills</li>
                                  <li>Quarterly disaster recovery exercises</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Network & Infrastructure -->
                      <div class="alert alert-info">
                        <h6 class="alert-heading">
                          <i class="mdi mdi-network me-2"></i>
                          Network & Infrastructure Resilience
                        </h6>
                        <div class="row">
                          <div class="col-md-6">
                            <ul>
                              <li><strong>Redundant Internet Connections:</strong> Primary fiber + backup 4G/5G</li>
                              <li><strong>Load Balancers:</strong> Active-passive configuration with health checks</li>
                              <li><strong>DNS Failover:</strong> Automatic DNS updates for service continuity</li>
                            </ul>
                          </div>
                          <div class="col-md-6">
                            <ul>
                              <li><strong>Power Systems:</strong> UPS + diesel generators for 72-hour autonomy</li>
                              <li><strong>Cooling Systems:</strong> Redundant HVAC with temperature monitoring</li>
                              <li><strong>Physical Security:</strong> 24/7 monitoring with access controls</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Breaches Tab -->
            <div class="tab-pane fade" id="security-breaches" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card border-danger">
                    <div class="card-header bg-danger text-white">
                      <h5 class="mb-0 text-white">
                        <i class="mdi mdi-security me-2"></i>
                        Security Breach Response
                      </h5>
                    </div>
                    <div class="card-body">
                      <!-- Breach Classification -->
                      <div class="mb-4">
                        <h6>Security Breach Classification</h6>
                        <div class="row">
                          <div class="col-md-4">
                            <div class="breach-type p-3 border border-danger rounded">
                              <h6 class="text-danger">Critical Breaches</h6>
                              <ul class="mb-0">
                                <li>Data exfiltration</li>
                                <li>System compromise</li>
                                <li>Privileged account takeover</li>
                                <li>Ransomware deployment</li>
                              </ul>
                              <div class="mt-2">
                                <span class="badge bg-danger">Response: Immediate</span>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-4">
                            <div class="breach-type p-3 border border-warning rounded">
                              <h6 class="text-warning">High Priority Breaches</h6>
                              <ul class="mb-0">
                                <li>Unauthorized access attempts</li>
                                <li>Malware detection</li>
                                <li>Suspicious user behavior</li>
                                <li>Network intrusions</li>
                              </ul>
                              <div class="mt-2">
                                <span class="badge bg-warning">Response: Within 1 hour</span>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-4">
                            <div class="breach-type p-3 border border-info rounded">
                              <h6 class="text-info">Medium Priority Breaches</h6>
                              <ul class="mb-0">
                                <li>Policy violations</li>
                                <li>Suspicious emails</li>
                                <li>Failed authentication patterns</li>
                                <li>Unusual network traffic</li>
                              </ul>
                              <div class="mt-2">
                                <span class="badge bg-info">Response: Within 4 hours</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Response Procedures -->
                      <div class="mb-4">
                        <h6>Security Breach Response Procedures</h6>
                        <div class="security-response-flow">
                          <div class="response-step mb-3 p-3 border-start border-5 border-danger">
                            <h6 class="text-danger">IMMEDIATE (0-15 minutes)</h6>
                            <div class="row">
                              <div class="col-md-6">
                                <strong>Containment Actions:</strong>
                                <ul>
                                  <li>Isolate affected systems from network</li>
                                  <li>Disable compromised user accounts</li>
                                  <li>Block suspicious IP addresses</li>
                                  <li>Activate incident response team</li>
                                </ul>
                              </div>
                              <div class="col-md-6">
                                <strong>Communication:</strong>
                                <ul>
                                  <li>Alert security team and management</li>
                                  <li>Notify IT operations center</li>
                                  <li>Document initial findings</li>
                                  <li>Preserve evidence and logs</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          <div class="response-step mb-3 p-3 border-start border-5 border-warning">
                            <h6 class="text-warning">INVESTIGATION (15-60 minutes)</h6>
                            <div class="row">
                              <div class="col-md-6">
                                <strong>Forensic Analysis:</strong>
                                <ul>
                                  <li>Collect system memory dumps</li>
                                  <li>Analyze network traffic logs</li>
                                  <li>Review security event logs</li>
                                  <li>Identify attack vectors and scope</li>
                                </ul>
                              </div>
                              <div class="col-md-6">
                                <strong>Impact Assessment:</strong>
                                <ul>
                                  <li>Determine data affected</li>
                                  <li>Assess system integrity</li>
                                  <li>Identify compromised credentials</li>
                                  <li>Evaluate business impact</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          <div class="response-step mb-3 p-3 border-start border-5 border-info">
                            <h6 class="text-info">ERADICATION (1-4 hours)</h6>
                            <div class="row">
                              <div class="col-md-6">
                                <strong>Threat Removal:</strong>
                                <ul>
                                  <li>Remove malware and backdoors</li>
                                  <li>Patch security vulnerabilities</li>
                                  <li>Update security configurations</li>
                                  <li>Strengthen access controls</li>
                                </ul>
                              </div>
                              <div class="col-md-6">
                                <strong>System Hardening:</strong>
                                <ul>
                                  <li>Reset compromised passwords</li>
                                  <li>Update firewall rules</li>
                                  <li>Deploy additional monitoring</li>
                                  <li>Implement compensating controls</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          <div class="response-step mb-3 p-3 border-start border-5 border-success">
                            <h6 class="text-success">RECOVERY (4-24 hours)</h6>
                            <div class="row">
                              <div class="col-md-6">
                                <strong>Service Restoration:</strong>
                                <ul>
                                  <li>Restore systems from clean backups</li>
                                  <li>Gradually reconnect to network</li>
                                  <li>Validate system integrity</li>
                                  <li>Resume normal operations</li>
                                </ul>
                              </div>
                              <div class="col-md-6">
                                <strong>Monitoring:</strong>
                                <ul>
                                  <li>Enhanced security monitoring</li>
                                  <li>Watch for indicators of compromise</li>
                                  <li>Monitor system performance</li>
                                  <li>Verify data integrity</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Legal & Compliance -->
                      <div class="legal-compliance p-3 bg-light rounded">
                        <h6>
                          <i class="mdi mdi-gavel me-2"></i>
                          Legal & Compliance Requirements
                        </h6>
                        <div class="row">
                          <div class="col-md-6">
                            <strong>Notification Requirements:</strong>
                            <ul>
                              <li>Data Protection Office: Within 2 hours</li>
                              <li>Affected customers: Within 24 hours</li>
                              <li>Regulatory authorities: Within 72 hours</li>
                              <li>Law enforcement: If criminal activity suspected</li>
                            </ul>
                          </div>
                          <div class="col-md-6">
                            <strong>Documentation Requirements:</strong>
                            <ul>
                              <li>Detailed incident timeline</li>
                              <li>Forensic analysis reports</li>
                              <li>Evidence preservation chain</li>
                              <li>Recovery and remediation actions</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Emergency Contacts Tab -->
            <div class="tab-pane fade" id="contacts" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <h5 class="mb-0 text-white">
                        <i class="mdi mdi-phone me-2"></i>
                        Emergency Contacts & Escalation
                      </h5>
                    </div>
                    <div class="card-body">
                      <!-- Internal Contacts -->
                      <div class="mb-4">
                        <h6>Internal Emergency Contacts</h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="contact-group p-3 border rounded">
                              <h6 class="text-danger">Critical Incident Response Team</h6>
                              <div class="contact-item mb-3">
                                <strong>Chief Information Security Officer (CISO)</strong>
                                <p class="mb-1">John Kamau</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-456
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>john.kamau@county.go.ke
                                </p>
                              </div>

                              <div class="contact-item mb-3">
                                <strong>IT Director</strong>
                                <p class="mb-1">Sarah Wanjiku</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-457
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>sarah.wanjiku@county.go.ke
                                </p>
                              </div>

                              <div class="contact-item">
                                <strong>System Administrator</strong>
                                <p class="mb-1">David Ochieng</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-458
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>david.ochieng@county.go.ke
                                </p>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-6">
                            <div class="contact-group p-3 border rounded">
                              <h6 class="text-warning">Secondary Response Team</h6>
                              <div class="contact-item mb-3">
                                <strong>Database Administrator</strong>
                                <p class="mb-1">Mary Njeri</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-459
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>mary.njeri@county.go.ke
                                </p>
                              </div>

                              <div class="contact-item mb-3">
                                <strong>Network Administrator</strong>
                                <p class="mb-1">Peter Kimani</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-460
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>peter.kimani@county.go.ke
                                </p>
                              </div>

                              <div class="contact-item">
                                <strong>Application Support Lead</strong>
                                <p class="mb-1">Grace Muthoni</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-700-123-461
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>grace.muthoni@county.go.ke
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- External Contacts -->
                      <div class="mb-4">
                        <h6>External Emergency Contacts</h6>
                        <div class="row">
                          <div class="col-md-6">
                            <div class="contact-group p-3 border rounded">
                              <h6 class="text-info">Vendor Support</h6>
                              <div class="contact-item mb-3">
                                <strong>Cloud Provider (AWS)</strong>
                                <p class="mb-1">Enterprise Support</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+1-800-AWS-SUPPORT
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-web me-1"></i>console.aws.amazon.com/support
                                </p>
                              </div>

                              <div class="contact-item mb-3">
                                <strong>Database Vendor (PostgreSQL)</strong>
                                <p class="mb-1">Enterprise DB Support</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+1-781-357-3390
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>support@enterprisedb.com
                                </p>
                              </div>

                              <div class="contact-item">
                                <strong>Security Vendor (Firewall)</strong>
                                <p class="mb-1">Fortinet Support</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-20-123-4567
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>support@fortinet.com
                                </p>
                              </div>
                            </div>
                          </div>

                          <div class="col-md-6">
                            <div class="contact-group p-3 border rounded">
                              <h6 class="text-success">Government & Regulatory</h6>
                              <div class="contact-item mb-3">
                                <strong>Kenya Computer Incident Response Team</strong>
                                <p class="mb-1">KE-CIRT</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-20-4242000
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>info@ke-cirt.go.ke
                                </p>
                              </div>

                              <div class="contact-item mb-3">
                                <strong>Data Protection Office</strong>
                                <p class="mb-1">Office of the Data Protection Commissioner</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>+254-20-2628000
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>info@odpc.go.ke
                                </p>
                              </div>

                              <div class="contact-item">
                                <strong>Law Enforcement</strong>
                                <p class="mb-1">Cybercrime Unit - DCI</p>
                                <p class="mb-1">
                                  <i class="mdi mdi-phone me-1"></i>999 / 112
                                </p>
                                <p class="mb-0">
                                  <i class="mdi mdi-email me-1"></i>cybercrime@dci.go.ke
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Escalation Matrix -->
                      <div class="escalation-matrix">
                        <h6>Escalation Matrix</h6>
                        <div class="table-responsive">
                          <table class="table table-bordered">
                            <thead class="table-dark">
                            <tr>
                              <th>Incident Severity</th>
                              <th>Initial Response</th>
                              <th>15 Minutes</th>
                              <th>1 Hour</th>
                              <th>4 Hours</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr class="table-danger">
                              <td><strong>Critical</strong></td>
                              <td>System Admin + CISO</td>
                              <td>IT Director</td>
                              <td>County CEC</td>
                              <td>Governor's Office</td>
                            </tr>
                            <tr class="table-warning">
                              <td><strong>High</strong></td>
                              <td>System Admin</td>
                              <td>CISO</td>
                              <td>IT Director</td>
                              <td>County CEC</td>
                            </tr>
                            <tr class="table-info">
                              <td><strong>Medium</strong></td>
                              <td>On-call Engineer</td>
                              <td>System Admin</td>
                              <td>CISO</td>
                              <td>IT Director</td>
                            </tr>
                            <tr class="table-success">
                              <td><strong>Low</strong></td>
                              <td>On-call Engineer</td>
                              <td>System Admin</td>
                              <td>System Admin</td>
                              <td>CISO</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Communication Channels -->
                      <div class="alert alert-primary mt-4">
                        <h6 class="alert-heading">
                          <i class="mdi mdi-message-alert me-2"></i>
                          Emergency Communication Channels
                        </h6>
                        <div class="row">
                          <div class="col-md-6">
                            <ul>
                              <li><strong>Primary:</strong> Dedicated emergency phone line</li>
                              <li><strong>Secondary:</strong> WhatsApp incident group</li>
                              <li><strong>Backup:</strong> Telegram secure channel</li>
                            </ul>
                          </div>
                          <div class="col-md-6">
                            <ul>
                              <li><strong>Email:</strong> emergency@county.go.ke (auto-forwards)</li>
                              <li><strong>SMS Alerts:</strong> Mass notification system</li>
                              <li><strong>Dashboard:</strong> Real-time incident status board</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="downloadPlan" class="btn btn-success">
                <i class="mdi mdi-download me-1"></i>
                Download Plan (PDF)
              </button>
              <button @click="printPlan" class="btn btn-info ms-2">
                <i class="mdi mdi-printer me-1"></i>
                Print Plan
              </button>
            </div>
            <div>
              <button @click="updatePlan" class="btn btn-warning me-2">
                <i class="mdi mdi-pencil me-1"></i>
                Update Plan
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "IncidentResponsePlanModal",
  methods: {
    downloadPlan() {
      // Simulate plan download
      alert('Downloading Incident Response Plan as PDF...');
      this.$emit('plan-updated');
    },

    printPlan() {
      // Simulate plan printing
      window.print();
    },

    updatePlan() {
      // Simulate plan update
      alert('Opening plan editor for updates...');
      this.$emit('plan-updated');
    }
  }
}
</script>

<style scoped>
.timeline-item {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.timeline-marker {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
}

.timeline-content {
  flex-grow: 1;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: bold;
  margin-right: 10px;
}

.step-item {
  display: flex;
  align-items: center;
}

.component-item,
.contact-group,
.breach-type,
.recovery-scenario {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.component-item:hover,
.contact-group:hover,
.breach-type:hover,
.recovery-scenario:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  border-bottom-color: #0d6efd;
  font-weight: 600;
}

.table th {
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
}

.modal-xl {
  max-width: 1200px;
}

.tab-content {
  min-height: 500px;
}

.recovery-metrics {
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border-top: 1px solid #dee2e6;
}

.alert-heading {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.contact-item {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
}

.escalation-matrix .table {
  font-size: 0.875rem;
}

.response-step {
  background: #f8f9fa;
}

@media print {
  .modal-header,
  .modal-footer,
  .btn {
    display: none !important;
  }

  .modal-dialog {
    max-width: 100% !important;
    margin: 0 !important;
  }

  .modal-content {
    border: none !important;
    box-shadow: none !important;
  }
}
</style>